import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'

/**
 * `useState` that survives reloads by persisting to localStorage under `key`.
 *
 * localStorage, never a cookie: a cookie rides on every HTTP request and bloats the headers —
 * exactly what caused the 431 "request header too large" earlier. UI preferences belong here.
 *
 * Falls back to `initial` when the key is unset or the stored value can't be parsed. Guards every
 * access so private-mode / disabled-storage never throws.
 */
export function usePersistedState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw != null ? (JSON.parse(raw) as T) : initial
    } catch {
      return initial
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {
      /* storage full or unavailable — a lost preference is not worth throwing over */
    }
  }, [key, value])

  return [value, setValue]
}
